import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { reglageMenuItem } from '@teetsh/app/src/containers/Header/menus';
import {
  BookOpenIcon as BookOpen,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { SchoolIcon } from '@teetsh/app/src/components/Icons/icons/SchoolIcon';
import UserGroupIcon from '@heroicons/react/24/outline/UserGroupIcon';
import DocumentTextIcon from '@heroicons/react/24/outline/DocumentTextIcon';
import { routes } from '@teetsh/app/src/containers/routes';
import RectangleGroupIcon from '@heroicons/react/24/outline/RectangleGroupIcon';
import { useCountryConfig } from '@teetsh/app/src/queries/user/useCountryConfig';
import { useTranslation } from 'react-i18next';
import { PigIcon } from '@teetsh/app/src/components/Icons/icons/PigIcon';
import { PlannerIcon } from '@teetsh/app/src/components/Icons/icons/PlannerIcon';
import { CarnetIcon } from '@teetsh/app/src/components/Icons/icons/CarnetIcon';
import { ProgrammationIcon } from '@teetsh/app/src/components/Icons/icons/ProgrammationIcon';

type MenuItemId =
  | 'dashboard'
  | 'bo'
  | 'budgets'
  | 'programmations'
  | 'sequences'
  | 'cj'
  | 'edt'
  | 'carnets/livrets'
  | 'carnets'
  | 'livrets'
  | 'planner'
  | 'categories'
  | 'calendar'
  | 'eleves'
  | 'registre'
  | 'pyramide'
  | 'eleves-list'
  | 'trombinoscope'
  | 'schoolyears'
  | 'settings';

export interface MenuItem {
  id: MenuItemId;
  route: string;
  icon?: any;
  title: string;
  disabled: boolean;
  hidden?: boolean;
  isActiveFct?: (p: string) => boolean;
  subItems?: MenuItem[];
}

export function useGetTeacherRoutes(): MenuItem[] {
  const { access } = useUser();
  const { isMenuItemLsuEnabled } = useCountryConfig();
  const { t } = useTranslation(['common']);

  const programmationsMenuItem: MenuItem = {
    id: 'programmations',
    route: '/programmations',
    icon: ProgrammationIcon,
    title: t('common:sidebar.programmations'),
    disabled: false,
  };

  const fichePrepMenu: MenuItem = {
    id: 'sequences',
    route: '/sequences',
    icon: DocumentTextIcon,
    title: t('common:sidebar.fichesDePrep'),
    disabled: false,
    isActiveFct: (path: string) =>
      path.includes('sequences') || path.includes('fiches-de-prep'),
  };

  const edtMenuItem: MenuItem = {
    id: 'edt',
    route: '/emploi-du-temps',
    icon: CalendarDaysIcon,
    title: t('common:sidebar.emploiDuTemps'),
    disabled: false,
  };

  const cjMenuItem: MenuItem = {
    id: 'cj',
    route: '/cahier-journal',
    icon: BookOpen,
    title: t('common:sidebar.cahierJournal'),
    disabled: !access.hasAccessToCahierJournal,
  };

  const carnetsDeNotes: MenuItem = {
    id: 'carnets/livrets',
    route: '/carnets-de-notes',
    icon: CarnetIcon,
    title: t('common:sidebar.carnetLivret.title'),
    disabled: false,
    subItems: [
      {
        id: 'carnets',
        route: '/carnets-de-notes',
        title: t('common:sidebar.carnetLivret.carnetsDeNotes'),
        disabled: false,
      },
      {
        id: 'livrets',
        route: '/livrets',
        title: t('common:sidebar.carnetLivret.livrets'),
        disabled: false,
        hidden: !isMenuItemLsuEnabled,
      },
    ],
  };

  const elevesMenuItem: MenuItem = {
    id: 'eleves',
    route: '/eleves',
    icon: UserGroupIcon,
    title: t('common:sidebar.eleves.title'),
    disabled: false,
    subItems: [
      {
        id: 'eleves-list',
        route: '/eleves',
        title: t('common:sidebar.eleves.list'),
        disabled: false,
        isActiveFct: (path: string) =>
          path.includes('eleves') &&
          !path.includes('livrets') &&
          !path.includes('carnets-de-notes'),
      },
      {
        id: 'pyramide',
        route: '/pyramides-des-ages',
        title: t('common:sidebar.eleves.pyramideDesAges'),
        disabled: !access.hasAccessToPyramide,
      },
      {
        id: 'registre',
        route: '/registre-appel',
        title: t('common:sidebar.eleves.registreAppel'),
        disabled: false,
      },
      {
        id: 'trombinoscope',
        route: '/trombinoscope',
        title: t('common:sidebar.eleves.trombinoscope'),
        disabled: !access.hasAccessToTrombinoscope,
      },
    ],
  };

  const anneeScolaireMenuItem: MenuItem = {
    id: 'schoolyears',
    route: routes.schoolyearsSettings,
    icon: SchoolIcon,
    title: t('common:sidebar.anneesScolaires'),
    disabled: false,
  };

  const budgetClassMenuItem: MenuItem = {
    id: 'budgets',
    route: '/budgets',
    icon: PigIcon,
    title: t('common:sidebar.budgets'),
    disabled: false,
  };

  const plannerMenuItem: MenuItem = {
    id: 'planner',
    route: '/planner',
    icon: PlannerIcon,
    title: t('common:sidebar.planner.title'),
    disabled: false,
    subItems: [
      {
        id: 'categories',
        route: '/planner/categories',
        title: t('common:sidebar.planner.vue108h'),
        disabled: false,
      },
      {
        id: 'calendar',
        route: '/planner/calendar',
        title: t('common:sidebar.planner.vueCalendrier'),
        disabled: false,
      },
    ],
  };

  const dashboardMenuItem: MenuItem = {
    id: 'dashboard',
    route: '/dashboard',
    icon: RectangleGroupIcon,
    title: t('common:sidebar.dashboard'),
    disabled: false,
  };

  const settingsMenuItem: MenuItem = {
    ...reglageMenuItem,
    title: t('common:sidebar.settings'),
    id: 'settings',
    disabled: false,
    isActiveFct: (path: string) =>
      path.includes('settings') && !path.includes('mes-annees-scolaires'),
  };

  return [
    dashboardMenuItem,
    cjMenuItem,
    edtMenuItem,
    programmationsMenuItem,
    fichePrepMenu,
    elevesMenuItem,
    carnetsDeNotes,
    plannerMenuItem,
    budgetClassMenuItem,
    anneeScolaireMenuItem,
    settingsMenuItem,
  ];
}
